import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { provideTranslocoScope } from '@ngneat/transloco';
import { BillingAddress, MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING } from '@platform-frontend/marketplace';
import { MobileService } from '@platform-frontend/ui-tools';
import { MatomoTracker } from 'ngx-matomo-client';
import { CHECKOUT_TRANSLOCO_SCOPE } from '../../checkout-transloco-scope';

@Component({
  selector: 'yat-billing-address-panel',
  templateUrl: './billing-address-panel.component.html',
  styleUrls: ['./billing-address-panel.component.scss'],
  providers: [provideTranslocoScope(CHECKOUT_TRANSLOCO_SCOPE)]
})
export class BillingAddressPanelComponent implements OnChanges {
  constructor(private element: ElementRef, private mobileService: MobileService, private matomoTracker: MatomoTracker) {}
  @Input()
  id: string;

  @Input()
  billingAddress: BillingAddress;

  @Input()
  minified: boolean = false;

  @Input()
  defaultIcon: 'selected' | 'settings' | null = null;

  @Input()
  showDeleteIcon: boolean = true;

  @Input()
  showEditIcon: boolean = true;

  @Input()
  liveLicense: boolean = true;

  @Input()
  disableHover: boolean = false;

  @Input()
  sameBillingAddressSelected: boolean = false;

  @Input()
  matomoEventClickAction: string;

  @Output()
  modalClosed: EventEmitter<void> = new EventEmitter();

  @Output()
  selectAddress: EventEmitter<() => void> = new EventEmitter();

  @Output()
  editMethod: EventEmitter<void> = new EventEmitter();

  @Output()
  prepareDeleteAddress: EventEmitter<void> = new EventEmitter();

  @Output()
  cancelDeleteAddress: EventEmitter<void> = new EventEmitter();

  @Output()
  deleteAddress: EventEmitter<() => void> = new EventEmitter();

  deleteMode: 'default' | 'delete' | 'deleteInProgress' = 'default';
  selectMode: 'default' | 'selectInProgress' = 'default';
  showSecondConfirmDialogFlag: boolean = false;
  spinnerShown: boolean = false;

  readonly MATOMO_EVENT_CATEGORY_BILLING = MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING;

  private preSelectEvent: Event | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.deleteMode === 'delete' && !this.element.nativeElement.contains(event.target)) {
      this.abortConfirmDelete();
    }
  }

  showSecondConfirmDialog(): void {
    this.showSecondConfirmDialogFlag = true;
  }

  deleteItem(): void {
    this.delete();
    this.abortConfirmDelete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.sameBillingAddressSelected) {
      if (this.sameBillingAddressSelected && this.isMobile()) {
        this.updateComponent();
      }
    }
  }

  updateComponent(): void {
    this.settingsClicked();
  }

  selectBillingAddress(event: Event): void {
    if (this.isMobile() && this.preSelectEvent !== null) {
      this.preSelectEvent = event;
    } else {
      this.select(event);
    }
  }

  select(event: Event): void {
    const target = event.target as HTMLElement;
    // click only in the selectable area - not buttons
    if (!target.matches('.no-select, .no-select *')) {
      if (this.defaultIcon === null) {
        this.selectMode = 'selectInProgress';
      }
      this.selectAddress.next(() => (this.selectMode = 'default'));
    }
  }

  settingsClicked(event?: Event): void {
    event?.stopPropagation();
    this.deleteMode = 'delete';
  }

  isMobile(): boolean {
    return this.mobileService.isMobileScreenSize();
  }

  prepareDelete(): void {
    this.deleteMode = 'delete';
    this.prepareDeleteAddress.next();
  }

  delete(completeCallback?: () => void): void {
    this.showSecondConfirmDialogFlag = false;
    this.spinnerShown = true;
    this.deleteMode = 'deleteInProgress';
    this.deleteAddress.next(() => {
      this.completeDelete(completeCallback);
      this.spinnerShown = false;
    });

    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_BILLING, 'Confirm_DeleteBillingAddress');
  }

  private completeDelete(completeCallback?: () => void): void {
    if (completeCallback) completeCallback();
    this.deleteMode = 'deleteInProgress';
    this.modalClosed.emit();
  }

  abortConfirmDelete(event?: Event): void {
    event?.stopPropagation();
    this.showSecondConfirmDialogFlag = false;
    this.completeDelete();
    this.deleteMode = 'default';
    this.cancelDeleteAddress.next();
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_BILLING, 'Cancel_DeleteBillingAddress');
  }

  edit(): void {
    this.editMethod.emit();
  }
}
